// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useState } from 'react'

export type ToolMode = 'pointer' | 'grab' | 'select'

interface UseToolModeParams {
    brushEnabled: boolean
    spacePressed: boolean
}

interface UseToolModeResult {
    toolMode: ToolMode
    setToolMode: (mode: ToolMode) => void
    resetToolMode: () => void
    /** True when space is held OR toolMode is 'grab' — use for toolbar highlight */
    effectiveGrabHighlight: boolean
}

export function useToolMode(params: UseToolModeParams): UseToolModeResult {
    const { brushEnabled, spacePressed } = params
    const [toolMode, setToolMode] = useState<ToolMode>('pointer')

    useEffect(() => {
        if (brushEnabled) return

        const onKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null
            if (target) {
                const tag = target.tagName
                const isInputLike =
                    tag === 'INPUT' ||
                    tag === 'TEXTAREA' ||
                    target.isContentEditable
                if (isInputLike) return
            }
            if (e.metaKey || e.ctrlKey || e.altKey) return
            const key = e.key.toLowerCase()
            if (key === 'v') setToolMode('pointer')
            else if (key === 'h') setToolMode('grab')
            else if (key === 's') setToolMode('select')
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [brushEnabled])

    const resetToolMode = useCallback(() => {
        setToolMode('pointer')
    }, [])

    const effectiveGrabHighlight = spacePressed || toolMode === 'grab'

    return { toolMode, setToolMode, resetToolMode, effectiveGrabHighlight }
}
